import { api } from '@/lib/api'

import { isApiError, isNotFoundError } from '@/shared/api/utils/error'

import { toCollectorVolume, toFleetVolume } from './volume'

import type {
	AgentDetailDTO,
	AgentDTO,
	AgentLogSourceDTO,
	AgentLogSourceOptionDTO,
	AgentLogSourceVolumeDTO,
	AgentLogSourceWriteDTO,
	AgentVolumeDTO,
	CaptureTask,
	CaptureTaskDTO,
	CollectorDetailView,
	CollectorLogSourceSummary,
	CollectorNameConflict,
	CollectorSummary,
	CreateAgentLogSourceRequest,
	CreatedAgentDTO,
	DiagnosticTask,
	DiagnosticTaskDTO,
	EnrollmentToken,
	EnrollmentTokenDTO,
	UpdateAgentLogSourceRequest,
	VolumeReading,
} from './types'

/** One embedded listener, from wire shape to what the row renders. */
function toCollectorLogSourceSummary(
	source: AgentLogSourceDTO,
): CollectorLogSourceSummary {
	return {
		id: source.log_source_id,
		name: source.name,
		port: source.port,
		// Cast, not validated: an unrecognised value still reaches the badge,
		// which paints it neutral rather than blank (P-17, `resolvePillPreset`).
		status: source.status as CollectorLogSourceSummary['status'],
		statusMessage: source.status_message,
		silence: source.silence as CollectorLogSourceSummary['silence'],
		silenceMessage: source.silence_message,
	}
}

/** One collector row, from wire shape to what the list renders. */
function toCollectorSummary(agent: AgentDTO): CollectorSummary {
	return {
		id: agent.agent_id,
		name: agent.name,
		status: agent.status as CollectorSummary['status'],
		statusMessage: agent.status_message,
		lastSeen: agent.last_seen,
		agentVersion: agent.agent_version,
		logSources: agent.log_sources.map(toCollectorLogSourceSummary),
	}
}

/** One collector, from the detail route's wire shape to what the page renders. */
function toCollectorDetail(agent: AgentDetailDTO): CollectorDetailView {
	return {
		...toCollectorSummary({ ...agent, log_sources: [] }),
		hostname: agent.hostname,
		upToDate: agent.up_to_date,
		targetAgentVersion: agent.target_agent_version,
		logSources: agent.log_sources.map((source) => ({
			...toCollectorLogSourceSummary(source),
			lastEventAt: source.last_event_at,
		})),
	}
}

/**
 * The Collectors **list**, with the Log Sources hosted on each — `GET /agents`
 * (API quickstart §4.1–4.2). One call: every collector, its
 * resolved status and its log sources arrive embedded, so there is no second
 * request to join.
 *
 * The Add Log Source form and the New Integration catalog's Network Log card
 * read it too: its embedded listeners are what the port-conflict check and the
 * card's choice of default Collector run against.
 *
 * `signal` is honoured so a tenant switch or a fast back-navigation cancels
 * the request (P-03).
 */
export async function fetchCollectorSummaries(
	tenant: string,
	signal?: AbortSignal,
): Promise<CollectorSummary[]> {
	const { data } = await api.get<{ agents: AgentDTO[] }>(
		`/api/tenants/${tenant}/agents`,
		{ signal },
	)
	return data.agents.map(toCollectorSummary)
}

/**
 * The device families this tenant may add a Log Source for —
 * `GET /agent-log-source-options` (API quickstart §5.1).
 *
 * An empty list is not "nothing configured yet": the backend answers empty
 * exactly when the tenant's plan excludes syslog collection, so it is the
 * plan signal (`../plan-upsell.ts`).
 */
export async function fetchLogSourceOptions(
	tenant: string,
	signal?: AbortSignal,
): Promise<AgentLogSourceOptionDTO[]> {
	const { data } = await api.get<{ options: AgentLogSourceOptionDTO[] }>(
		`/api/tenants/${tenant}/agent-log-source-options`,
		{ signal },
	)
	return data.options
}

/**
 * Add a listener to a collector — `POST /agent-log-sources` (API quickstart
 * §5.3). No `AbortSignal`: a write that is cancelled in flight leaves the form
 * with neither a result nor an error (P-03).
 *
 * Failures reach the caller untouched; `./log-source-write-errors.ts` reads
 * them, because the server's answer is the authority on every one of them.
 */
export async function createLogSource(
	tenant: string,
	body: CreateAgentLogSourceRequest,
): Promise<AgentLogSourceWriteDTO> {
	const { data } = await api.post<AgentLogSourceWriteDTO>(
		`/api/tenants/${tenant}/agent-log-sources`,
		body,
	)
	return data
}

/**
 * Change a listener's name, port or enabled state — `PATCH
 * /agent-log-sources/{id}` (API quickstart §5.4). Turning a listener off and
 * back on is this same route with `enabled` alone.
 *
 * Failures reach the caller untouched, read by `./log-source-write-errors.ts`
 * exactly as a create's are.
 */
export async function updateLogSource(
	tenant: string,
	sourceId: string,
	body: UpdateAgentLogSourceRequest,
): Promise<AgentLogSourceWriteDTO> {
	const { data } = await api.patch<AgentLogSourceWriteDTO>(
		`/api/tenants/${tenant}/agent-log-sources/${encodeURIComponent(sourceId)}`,
		body,
	)
	return data
}

/**
 * Remove a listener — `DELETE /agent-log-sources/{id}` (API quickstart §5.5).
 * Answers `204` straight away: unlike a collector's delete there is no 449
 * handshake, so no passphrase is ever asked for.
 */
export async function deleteLogSource(
	tenant: string,
	sourceId: string,
): Promise<void> {
	await api.delete(
		`/api/tenants/${tenant}/agent-log-sources/${encodeURIComponent(sourceId)}`,
	)
}

/**
 * One Collector and every listener on it — `GET /agents/{agent_id}` (API
 * quickstart §4.2). A list row plus `hostname`, the version comparison, and
 * each listener's `last_event_at`.
 *
 * A 404 resolves to `null`, the page's designed not-found state: a pasted or
 * stale link is an expected outcome, not an error for the boundary. The 404 is
 * the only not-found signal — an unknown or foreign collector's *log sources*
 * read would answer an empty list, indistinguishable from a real collector with
 * no listeners yet, so emptiness never means not-found here.
 */
export async function fetchCollectorDetail(
	tenant: string,
	collectorId: string,
	signal?: AbortSignal,
): Promise<CollectorDetailView | null> {
	try {
		const { data } = await api.get<AgentDetailDTO>(
			`/api/tenants/${tenant}/agents/${encodeURIComponent(collectorId)}`,
			{ signal },
		)
		return toCollectorDetail(data)
	} catch (error) {
		if (isNotFoundError(error)) return null
		throw error
	}
}

/**
 * Rename one Collector — `PATCH /agents/{agent_id}` (API quickstart §3.4).
 *
 * The name is the only field that changes: the id, the credential and the
 * history stay, and the host is never told. A name another collector in the
 * tenant holds answers 409, whose message the form shows as-is. Resolves to
 * nothing: the caller refetches rather than reading this route's row, which
 * carries no listeners.
 *
 * No `AbortSignal`: an action, not a read (P-03).
 */
export async function renameCollector(
	tenant: string,
	collectorId: string,
	name: string,
): Promise<void> {
	await api.patch(
		`/api/tenants/${tenant}/agents/${encodeURIComponent(collectorId)}`,
		{ name },
	)
}

/**
 * Delete one Collector and every listener on it — `DELETE /agents/{agent_id}`
 * (API quickstart §3.5).
 *
 * Always answers 449 first; the API client's confirmation flow shows the
 * warning and takes the passphrase, then retries. What reaches the caller is
 * the retry's answer, or the 449 itself when the user cancels.
 */
export async function deleteCollector(
	tenant: string,
	collectorId: string,
): Promise<void> {
	await api.delete(
		`/api/tenants/${tenant}/agents/${encodeURIComponent(collectorId)}`,
	)
}

/**
 * Name a new Collector — `POST /agents` (API quickstart §3.1). Mints no
 * credential: until {@link issueEnrollmentToken} runs, the collector exists
 * as `enrolling` with no way to install it.
 *
 * A duplicate name rejects with a 409; {@link readCollectorNameConflict} reads
 * which collector holds it. No `AbortSignal` (P-03).
 */
export async function createCollector(
	tenant: string,
	name: string,
): Promise<Pick<CollectorSummary, 'id' | 'name' | 'status'>> {
	const { data } = await api.post<CreatedAgentDTO>(
		`/api/tenants/${tenant}/agents`,
		{ name },
	)
	return {
		id: data.agent_id,
		name: data.name,
		status: data.status as CollectorSummary['status'],
	}
}

/**
 * Mint an enrollment token and the install command that carries it —
 * `POST /agents/{agent_id}/enrollment-token` (API quickstart §3.1).
 *
 * `confirmReplace` must be true for a collector that already enrolled: the
 * call retires the credential it is running on. The backend answers 409
 * without it, so the caller decides from the collector's status, before the
 * call, whether to ask. No `AbortSignal` (P-03).
 */
export async function issueEnrollmentToken(
	tenant: string,
	collectorId: string,
	confirmReplace: boolean,
): Promise<EnrollmentToken> {
	const { data } = await api.post<EnrollmentTokenDTO>(
		`/api/tenants/${tenant}/agents/${encodeURIComponent(collectorId)}/enrollment-token`,
		confirmReplace ? { confirm_replace: true } : {},
	)
	return {
		collectorId: data.agent_id,
		installCommand: data.install_command,
		expiresAt: data.enrollment_token_expires_at,
		replacedEnrolledKey: data.replaced_enrolled_key,
	}
}

/**
 * The collector a duplicate-name 409 from {@link createCollector} points at,
 * or `null` for any other failure. Read off `context.agent_id` and
 * `context.agent_status` in the error envelope.
 */
export function readCollectorNameConflict(
	error: unknown,
): CollectorNameConflict | null {
	if (!isApiError(error) || error.status !== 409) return null

	const envelope = error.data as
		| { error?: { context?: { agent_id?: unknown; agent_status?: unknown } } }
		| undefined
	const context = envelope?.error?.context
	if (typeof context?.agent_id !== 'string') return null

	return {
		collectorId: context.agent_id,
		status: context.agent_status === 'enrolled' ? 'enrolled' : 'enrolling',
		message: error.message,
	}
}

/**
 * Ask one Collector to restart — `POST /agents/{agent_id}/restart` (API
 * quickstart §3.6). A full restart, not a reload: records buffered on the
 * host are dropped.
 *
 * Always answers 449 first; the API client's confirmation flow shows the
 * warning and takes the passphrase, then retries. What reaches the caller is
 * the retry's answer, or the 449 itself when the user cancels.
 *
 * The 202 means only that the request was queued for the collector's next
 * check-in, and it expires unsent after three minutes. It carries no task id
 * and nothing reports on the restart, so this resolves to nothing.
 */
export async function restartCollector(
	tenant: string,
	collectorId: string,
): Promise<void> {
	await api.post(
		`/api/tenants/${tenant}/agents/${encodeURIComponent(collectorId)}/restart`,
	)
}

/**
 * The tenant's trailing-24h log volume, per collector — `GET /agents/volume`
 * (API quickstart §7.1).
 *
 * Always the current tenant, never `all`: the list it sits above is one
 * tenant's, and the per-collector route below refuses `all` outright.
 */
export async function fetchFleetVolume(
	tenant: string,
	signal?: AbortSignal,
): Promise<VolumeReading> {
	const { data } = await api.get<AgentVolumeDTO>(
		`/api/tenants/${tenant}/agents/volume`,
		{ signal },
	)
	return toFleetVolume(data)
}

/**
 * One collector's trailing-24h log volume, per log source —
 * `GET /agents/{agent_id}/volume` (API quickstart §7.1).
 *
 * A 404 is **not** mapped to `null` here, unlike `fetchCollectorDetail`: the
 * page reads this only once the detail route has found the collector, so a
 * 404 now means the collector went away between the two reads — a failure
 * for the volume tile to show, not a not-found page.
 */
export async function fetchCollectorVolume(
	tenant: string,
	collectorId: string,
	signal?: AbortSignal,
): Promise<VolumeReading> {
	const { data } = await api.get<AgentLogSourceVolumeDTO>(
		`/api/tenants/${tenant}/agents/${encodeURIComponent(collectorId)}/volume`,
		{ signal },
	)
	return toCollectorVolume(data)
}

/**
 * One capture task, from wire shape to the outcome the panel draws.
 *
 * `waiting` is read first and on its own, as the quickstart asks (§6.2): the
 * state list behind it is the backend's to grow. A re-dispatched task reads
 * `dispatched` again, which is still waiting, not a regression.
 */
function toCaptureTask(task: CaptureTaskDTO): CaptureTask {
	const taskId = task.task_id
	if (task.waiting) return { taskId, outcome: 'waiting' }

	const report = task.report ?? {}

	if (task.state === 'expired')
		return { taskId, outcome: 'expired', summary: report.summary ?? null }

	if (task.state === 'fulfilled' && task.verdict !== 'fail') {
		return {
			taskId,
			outcome: 'captured',
			events: report.events ?? [],
			cappedMessage: report.capped ? (report.message ?? null) : null,
			completedAt: task.completed_at,
		}
	}

	// `verdict: fail` is the collector saying it could not run the capture; a
	// final state this code does not know lands here too, rather than on a
	// spinner that would never stop.
	return { taskId, outcome: 'failed', error: report.error ?? null }
}

/**
 * Ask a Log Source's collector for a bounded sample of what is arriving —
 * `POST /agent-log-sources/{id}/captures` (API quickstart §6.1). No body: the
 * bounds are the backend's, not a caller choice.
 *
 * Answers with the task, **not the result**; poll {@link fetchCaptureTask}.
 * No `AbortSignal`: an action, not a read (P-03).
 */
export async function requestCapture(
	tenant: string,
	sourceId: string,
): Promise<CaptureTask> {
	const { data } = await api.post<CaptureTaskDTO>(
		`/api/tenants/${tenant}/agent-log-sources/${encodeURIComponent(sourceId)}/captures`,
	)
	return toCaptureTask(data)
}

/** One capture task, read back — `GET /captures/{task_id}` (API quickstart §6.2). */
export async function fetchCaptureTask(
	tenant: string,
	taskId: string,
	signal?: AbortSignal,
): Promise<CaptureTask> {
	const { data } = await api.get<CaptureTaskDTO>(
		`/api/tenants/${tenant}/captures/${encodeURIComponent(taskId)}`,
		{ signal },
	)
	return toCaptureTask(data)
}

/** A diagnostic task, from wire shape to what the panel renders. */
function toDiagnosticTask(task: DiagnosticTaskDTO): DiagnosticTask {
	const isExpired = task.state === 'expired'

	return {
		id: task.task_id,
		// `waiting` rather than the state list, as the route asks: a not-yet
		// state added later still reads as a wait.
		outcome: task.waiting ? 'waiting' : isExpired ? 'expired' : 'fulfilled',
		verdict: task.verdict,
		checks: (task.report?.checks ?? []).map((check) => ({
			name: check.name,
			status: check.status,
			summary: check.summary,
			evidence: check.evidence ?? [],
		})),
		expiredSummary: isExpired ? (task.report?.summary ?? null) : null,
		requestedAt: task.created_at,
	}
}

/**
 * Ask a log source's collector to diagnose it — `POST
 * /agent-log-sources/{log_source_id}/diagnostics` (API quickstart §6.1).
 *
 * Answers `202` with the task, never the result: the ask rides down on the
 * collector's next heartbeat, so the caller polls {@link fetchDiagnostic}.
 * `peer` is left out when blank, which is what skips the reachability check.
 * A malformed address answers 400 with a message written for the operator.
 *
 * No `AbortSignal`: an action, not a read (P-03).
 */
export async function requestDiagnostic(
	tenant: string,
	sourceId: string,
	peer: string,
): Promise<DiagnosticTask> {
	const trimmed = peer.trim()
	const { data } = await api.post<DiagnosticTaskDTO>(
		`/api/tenants/${tenant}/agent-log-sources/${encodeURIComponent(sourceId)}/diagnostics`,
		trimmed ? { peer: trimmed } : {},
	)
	return toDiagnosticTask(data)
}

/**
 * One diagnostic task and, once answered, its report — `GET
 * /diagnostics/{task_id}` (API quickstart §6.2).
 *
 * A 404 resolves to `null`: the id is one this browser kept, and a task the
 * backend no longer has is a run to forget, not an error for the panel.
 */
export async function fetchDiagnostic(
	tenant: string,
	taskId: string,
	signal?: AbortSignal,
): Promise<DiagnosticTask | null> {
	try {
		const { data } = await api.get<DiagnosticTaskDTO>(
			`/api/tenants/${tenant}/diagnostics/${encodeURIComponent(taskId)}`,
			{ signal },
		)
		return toDiagnosticTask(data)
	} catch (error) {
		if (isNotFoundError(error)) return null
		throw error
	}
}
